class PDPAccordion extends HTMLElement {
  constructor() {
    super();
    this._isInitialized = false;
    this._originalContent = null;
  }

  static get observedAttributes() {
    return [
      'heading',
      'icon'
    ];
  }

  connectedCallback() {
    if (!this._isInitialized) {
      // Store original content BEFORE rendering
      this._originalContent = this.innerHTML;
      this.render();
      this.setupEventListeners();
      this._isInitialized = true;

      // Check if accordion should be open by default
      if (this.hasAttribute('default-open')) {
        // Use setTimeout to ensure DOM is ready
        setTimeout(() => this.open(), 0);
      }
    }
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue !== newValue && this._isInitialized) {
      this.render();
      this.setupEventListeners();

      // Re-apply default open state if needed
      if (this.hasAttribute('default-open')) {
        setTimeout(() => this.open(), 0);
      }
    }
  }

  get heading() {
    return this.getAttribute('heading') || 'Accordion Heading';
  }

  get icon() {
    return this.getAttribute('icon') || 'plus';
  }

  render() {
    // Store original content
    // Use stored original content
    const content = this._originalContent || this.innerHTML;

    const iconSVG = this.icon === 'caret'
      ? `<svg class="pdp-accordion-icon icon-caret" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
          <path d="M11.9999 13.1714L16.9497 8.22168L18.3639 9.63589L11.9999 15.9999L5.63599 9.63589L7.0502 8.22168L11.9999 13.1714Z"></path>
        </svg>`
      : `<svg class="pdp-accordion-icon icon-plus" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <line class="horizontal" x1="4" y1="12" x2="20" y2="12" />
          <line class="vertical" x1="12" y1="4" x2="12" y2="20" />
        </svg>`;

    this.innerHTML = `
      <div class="pdp-accordion-header">
        <div class="pdp-accordion-header-content">
          <p>${this.heading}</p>
        </div>
        ${iconSVG}
      </div>

      <div class="pdp-accordion-content" data-state="closed">
        <div class="pdp-accordion-inner">
          ${content}
        </div>
      </div>
    `;
  }

  setupEventListeners() {
    const header = this.querySelector('.pdp-accordion-header');
    const content = this.querySelector('.pdp-accordion-content');
    
    if (!header || !content) return;

    header.addEventListener('click', () => {
      const isOpen = content.getAttribute('data-state') === 'open';
      if (isOpen) {
        this.close();
      } else {
        this.open();
      }
    });
  }

  // Public API
  open() {
    const content = this.querySelector('.pdp-accordion-content');
    const inner = this.querySelector('.pdp-accordion-inner');
    const icon = this.querySelector('.pdp-accordion-icon');

    if (content && inner) {
      if (content.getAttribute('data-state') === 'open') return;

      const height = inner.scrollHeight;
      content.style.setProperty('--pdp-accordion-content-height', `${height}px`);
      
      content.setAttribute('data-state', 'open');
      content.classList.remove('animate-accordion-up');
      content.classList.add('animate-accordion-down');
      
      if (icon) icon.classList.add('rotate');

      // Cleanup after animation to allow dynamic height (e.g. window resize)
      const onAnimationEnd = () => {
        if (content.getAttribute('data-state') === 'open') {
          content.style.height = 'auto';
          content.classList.remove('animate-accordion-down');
        }
        content.removeEventListener('animationend', onAnimationEnd);
      };
      content.addEventListener('animationend', onAnimationEnd, { once: true });
      
      this.dispatchEvent(new CustomEvent('accordion-open', { bubbles: true }));
    }
  }

  close() {
    const content = this.querySelector('.pdp-accordion-content');
    const icon = this.querySelector('.pdp-accordion-icon');

    if (content) {
      if (content.getAttribute('data-state') === 'closed') return;

      // Capture current height for the animation start point
      const height = content.offsetHeight;
      content.style.setProperty('--pdp-accordion-content-height', `${height}px`);
      content.style.height = `${height}px`;
      
      // Force reflow to ensure the height is applied before animation starts
      content.offsetHeight;

      content.setAttribute('data-state', 'closed');
      content.classList.remove('animate-accordion-down');
      content.classList.add('animate-accordion-up');

      if (icon) icon.classList.remove('rotate');

      const onAnimationEnd = () => {
        if (content.getAttribute('data-state') === 'closed') {
          content.style.height = '0';
          content.classList.remove('animate-accordion-up');
        }
        content.removeEventListener('animationend', onAnimationEnd);
      };
      content.addEventListener('animationend', onAnimationEnd, { once: true });

      this.dispatchEvent(new CustomEvent('accordion-close', { bubbles: true }));
    }
  }

  toggle() {
    const content = this.querySelector('.pdp-accordion-content');
    if (content) {
      const isOpen = content.getAttribute('data-state') === 'open';
      if (isOpen) {
        this.close();
      } else {
        this.open();
      }
    }
  }

  isOpen() {
    const content = this.querySelector('.pdp-accordion-content');
    return content ? content.getAttribute('data-state') === 'open' : false;
  }
}

// Register the custom element
customElements.define('pdp-accordion', PDPAccordion);
